import type { BusinessHours } from '@/types/settings';

/** Weekday keys as stored on BusinessHours, indexed by JS `Date.getDay()` (0=Sun). */
const WEEKDAY_KEYS: BusinessHours['day'][] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/** Default "Business hours" length when no business hours are configured. */
export const DEFAULT_FULL_DAY_MINUTES = 480;

/** Parse an "HH:mm" string to minutes since midnight, or null if unparseable. */
function parseHmToMinutes(value: string | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/** close − open in minutes for a given HH:mm pair, or null if invalid/non-positive. */
function spanMinutes(open: string | undefined, close: string | undefined): number | null {
  const openMin = parseHmToMinutes(open);
  const closeMin = parseHmToMinutes(close);
  if (openMin == null || closeMin == null) return null;
  const span = closeMin - openMin;
  return span > 0 ? Math.round(span) : null;
}

/**
 * Business-day length in minutes for `weekday` (0=Sunday … 6=Saturday).
 *
 * Resolution order:
 *  1. The per-weekday `businessHours` entry (when the day is open + valid).
 *  2. The global `businessHoursStart`/`businessHoursEnd` fallback.
 *  3. `DEFAULT_FULL_DAY_MINUTES` (480).
 */
export function businessDayMinutes(
  businessHours: BusinessHours[] | undefined | null,
  weekday: number,
  fallbackStart?: string,
  fallbackEnd?: string,
): number {
  const key = WEEKDAY_KEYS[((weekday % 7) + 7) % 7];
  const day = businessHours?.find((entry) => entry.day === key);
  if (day && day.open) {
    const span = spanMinutes(day.openTime, day.closeTime);
    if (span != null) return span;
  }
  const globalSpan = spanMinutes(fallbackStart, fallbackEnd);
  if (globalSpan != null) return globalSpan;
  return DEFAULT_FULL_DAY_MINUTES;
}
